import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { isString } from 'lodash';

const FIELD_ALIASES = {
  endpoint_id: 'party_tax_scheme.company_id',
};

const pascalSegmentToSnake = (segment) =>
  segment.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

const normalizeFieldKey = (key) => {
  const snakeCaseKey = key.split('.').map(pascalSegmentToSnake).join('.');
  return FIELD_ALIASES[snakeCaseKey] || FIELD_ALIASES[key] || snakeCaseKey;
};

const normalizeApiErrors = (errors) => {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  return Object.entries(errors).reduce((acc, [key, message]) => {
    if (message) {
      acc[normalizeFieldKey(key)] = message;
    }
    return acc;
  }, {});
};

export const mutateSupplier = async ({ payload, isEdit }) => {
  if (isEdit) {
    const { data } = await apiClient.put('/suppliers/' + payload.profile_id, payload);
    return data;
  } else {
    const { data } = await apiClient.post('/suppliers', payload);
    return data;
  }
};

export const useMutateSupplierHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: mutateSupplier,
    onSuccess: (data, variables) => {
      dispatch(
        showSnackbar({
          message: "Supplier saved successfully.",
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        const data = error.response?.data;

        if (status === 400 || status === 409) {
          const normalizedErrors = normalizeApiErrors(data?.errors);
          if (normalizedErrors && Object.keys(normalizedErrors).length > 0) {
            setError(normalizedErrors);
            return;
          }
        }

        dispatch(showSnackbar({ message: data?.message || data?.error || 'An unexpected error occurred', severity: 'error' }));
      } else {
        dispatch(
          showSnackbar({
            message: isString(error.message) ? error.message : JSON.stringify(error.message),
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (data, isEdit) => {
    mutation.mutate({ payload: data, isEdit });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
